// Renderer for metadata zones - transparent prisms

import * as THREE from 'three'
import type { GardenScene } from '@/scene/GardenScene'
import type { Zone } from '@/types/zone'
import { getZoneTypeDef, ZONE_OPACITY, ZONE_OUTLINE_WIDTH } from '@/constants/zoneTypes'
import { t } from '@/i18n'
import { debug } from '@/utils/debug'
import { DPM_SCALE } from '@/constants/coordinates'

export class ZoneRenderer {
  private scene: GardenScene
  private group: THREE.Group
  private meshes: Map<string, THREE.Group> = new Map()

  constructor(scene: GardenScene) {
    this.scene = scene

    // Create group for zones
    this.group = new THREE.Group()
    this.group.name = 'zones'

    // Add to scene's metadata group or directly to scene
    const threeScene = scene.getScene()
    threeScene.add(this.group)

    debug.log('ZoneRenderer', 'Initialized')
  }

  /**
   * Get the zones group (for layer visibility control)
   */
  getGroup(): THREE.Group {
    return this.group
  }

  /**
   * Render all zones
   */
  renderZones(zones: Zone[]): void {
    // Clear existing
    this.clear()

    for (const zone of zones) {
      this.renderZone(zone)
    }

    debug.log('ZoneRenderer', `Rendered ${zones.length} zones`)
  }

  /**
   * Render a single zone
   */
  renderZone(zone: Zone): void {
    const typeDef = getZoneTypeDef(zone.zoneType)
    const zoneGroup = new THREE.Group()
    zoneGroup.name = `zone_${zone.id}`

    if (zone.points && zone.points.length >= 3) {
      this.renderPolygonZone(zoneGroup, zone, typeDef)
    } else if (zone.shapeKind === 'ellipse') {
      this.renderEllipseZone(zoneGroup, zone, typeDef)
    } else {
      this.renderBoxZone(zoneGroup, zone, typeDef)
    }

    // Store zone data in userData for selection
    zoneGroup.userData.zoneData = zone
    zoneGroup.userData.zoneId = zone.id

    this.group.add(zoneGroup)
    this.meshes.set(zone.id, zoneGroup)

    debug.log('ZoneRenderer', `Rendered zone: ${zone.id} (${zone.zoneType})`)
  }

  /**
   * Render a rectangular box zone (original behavior)
   */
  private renderBoxZone(zoneGroup: THREE.Group, zone: Zone, typeDef: import('@/types/zone').ZoneTypeDefinition): void {
    const width = zone.width * DPM_SCALE
    const depth = zone.height * DPM_SCALE
    const height = zone.elevation

    const geometry = new THREE.BoxGeometry(width, height, depth)

    const fillMaterial = new THREE.MeshBasicMaterial({
      color: typeDef.color,
      transparent: true,
      opacity: ZONE_OPACITY,
      side: THREE.DoubleSide,
      depthWrite: false,
    })

    const prism = new THREE.Mesh(geometry, fillMaterial)
    zoneGroup.add(prism)

    const edgesMaterial = new THREE.LineBasicMaterial({
      color: typeDef.color,
      transparent: true,
      opacity: ZONE_OPACITY + 0.4,
      linewidth: 2,
    })
    const edges = new THREE.EdgesGeometry(geometry)
    const wireframe = new THREE.LineSegments(edges, edgesMaterial)
    wireframe.raycast = () => {} // Prevent default 1m Line threshold from inflating hitbox
    zoneGroup.add(wireframe)

    this.addEdgeTubes(zoneGroup, width, height, depth, typeDef.color)

    const label = zone.label || t(typeDef.labelKey, typeDef.label)
    const labelSprite = this.createLabelSprite(label, typeDef.color)
    labelSprite.position.set(0, height / 2 + 0.3, 0)
    zoneGroup.add(labelSprite)

    const centerX = zone.x * DPM_SCALE
    const centerZ = zone.y * DPM_SCALE
    zoneGroup.position.set(centerX, height / 2, centerZ)
    zoneGroup.rotation.y = -zone.rotation * Math.PI / 180
  }

  /**
   * Render an ellipse zone using ExtrudeGeometry
   */
  private renderEllipseZone(zoneGroup: THREE.Group, zone: Zone, typeDef: import('@/types/zone').ZoneTypeDefinition): void {
    const radiusX = (zone.width * DPM_SCALE) / 2
    const radiusZ = (zone.height * DPM_SCALE) / 2
    const height = zone.elevation

    // Build ellipse shape
    const curve = new THREE.EllipseCurve(0, 0, radiusX, radiusZ, 0, Math.PI * 2, false, 0)
    const curvePoints = curve.getPoints(48)
    const shape = new THREE.Shape(curvePoints)

    // Extrude to create 3D prism
    const extrudeGeometry = new THREE.ExtrudeGeometry(shape, {
      depth: height,
      bevelEnabled: false,
    })

    const fillMaterial = new THREE.MeshBasicMaterial({
      color: typeDef.color,
      transparent: true,
      opacity: ZONE_OPACITY,
      side: THREE.DoubleSide,
      depthWrite: false,
    })

    const prism = new THREE.Mesh(extrudeGeometry, fillMaterial)
    prism.rotation.x = -Math.PI / 2
    zoneGroup.add(prism)

    // Wireframe edges
    const edgesMaterial = new THREE.LineBasicMaterial({
      color: typeDef.color,
      transparent: true,
      opacity: ZONE_OPACITY + 0.4,
      linewidth: 2,
    })
    const edges = new THREE.EdgesGeometry(extrudeGeometry)
    const wireframe = new THREE.LineSegments(edges, edgesMaterial)
    wireframe.raycast = () => {} // Prevent default 1m Line threshold from inflating hitbox
    wireframe.rotation.x = -Math.PI / 2
    zoneGroup.add(wireframe)

    // Thick tube outlines for top and bottom ellipse rings
    this.addEllipseEdgeTubes(zoneGroup, radiusX, radiusZ, height, typeDef.color)

    // Label sprite on top
    const label = zone.label || t(typeDef.labelKey, typeDef.label)
    const labelSprite = this.createLabelSprite(label, typeDef.color)
    labelSprite.position.set(0, height + 0.3, 0)
    zoneGroup.add(labelSprite)

    // Position at zone center
    const centerX = zone.x * DPM_SCALE
    const centerZ = zone.y * DPM_SCALE
    zoneGroup.position.set(centerX, 0, centerZ)
    zoneGroup.rotation.y = -zone.rotation * Math.PI / 180
  }

  /**
   * Render a polygon zone using ExtrudeGeometry
   */
  private renderPolygonZone(zoneGroup: THREE.Group, zone: Zone, typeDef: import('@/types/zone').ZoneTypeDefinition): void {
    const points = zone.points!
    const height = zone.elevation

    // Build THREE.Shape from points (relative to center, in editor dots)
    // Negate Y: editor Y goes down, Three.js shape Y goes up
    const shape = new THREE.Shape()
    shape.moveTo(points[0].x * DPM_SCALE, -points[0].y * DPM_SCALE)
    for (let i = 1; i < points.length; i++) {
      shape.lineTo(points[i].x * DPM_SCALE, -points[i].y * DPM_SCALE)
    }
    shape.closePath()

    // Extrude to create 3D prism
    const extrudeGeometry = new THREE.ExtrudeGeometry(shape, {
      depth: height,
      bevelEnabled: false,
    })

    const fillMaterial = new THREE.MeshBasicMaterial({
      color: typeDef.color,
      transparent: true,
      opacity: ZONE_OPACITY,
      side: THREE.DoubleSide,
      depthWrite: false,
    })

    const prism = new THREE.Mesh(extrudeGeometry, fillMaterial)
    // Rotate so extrusion goes up (Y) instead of forward (Z)
    prism.rotation.x = -Math.PI / 2
    zoneGroup.add(prism)

    // Wireframe edges
    const edgesMaterial = new THREE.LineBasicMaterial({
      color: typeDef.color,
      transparent: true,
      opacity: ZONE_OPACITY + 0.4,
      linewidth: 2,
    })
    const edges = new THREE.EdgesGeometry(extrudeGeometry)
    const wireframe = new THREE.LineSegments(edges, edgesMaterial)
    wireframe.raycast = () => {} // Prevent default 1m Line threshold from inflating hitbox
    wireframe.rotation.x = -Math.PI / 2
    zoneGroup.add(wireframe)

    // Thick tube outlines
    this.addPolygonEdgeTubes(zoneGroup, points, height, typeDef.color)

    // Label sprite on top
    const label = zone.label || t(typeDef.labelKey, typeDef.label)
    const labelSprite = this.createLabelSprite(label, typeDef.color)
    labelSprite.position.set(0, height + 0.3, 0)
    zoneGroup.add(labelSprite)

    // Position at zone center
    const centerX = zone.x * DPM_SCALE
    const centerZ = zone.y * DPM_SCALE
    zoneGroup.position.set(centerX, 0, centerZ)
    zoneGroup.rotation.y = -zone.rotation * Math.PI / 180
  }

  /**
   * Create a text label sprite for a zone
   */
  private createLabelSprite(text: string, color: number): THREE.Sprite {
    const canvas = document.createElement('canvas')
    const size = 512
    canvas.width = size
    canvas.height = size / 4

    const ctx = canvas.getContext('2d')!

    // Background with rounded corners
    const colorStr = `#${color.toString(16).padStart(6, '0')}`
    ctx.fillStyle = colorStr
    this.roundRect(ctx, 4, 4, size - 8, size / 4 - 8, 16)
    ctx.fill()

    // Add slight border
    ctx.strokeStyle = 'rgba(0,0,0,0.3)'
    ctx.lineWidth = 2
    this.roundRect(ctx, 4, 4, size - 8, size / 4 - 8, 16)
    ctx.stroke()

    // Text
    ctx.fillStyle = this.getContrastColor(color)
    ctx.font = 'bold 48px -apple-system, BlinkMacSystemFont, sans-serif'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'

    // Truncate text if too long
    let displayText = text
    while (ctx.measureText(displayText).width > size - 32 && displayText.length > 3) {
      displayText = displayText.slice(0, -1)
    }
    if (displayText !== text) {
      displayText = displayText.slice(0, -1) + '...'
    }

    ctx.fillText(displayText, size / 2, size / 8)

    const texture = new THREE.CanvasTexture(canvas)
    texture.needsUpdate = true

    const material = new THREE.SpriteMaterial({
      map: texture,
      transparent: true,
      depthWrite: false,
    })

    const sprite = new THREE.Sprite(material)
    sprite.scale.set(1, 0.25, 1) // Aspect ratio 4:1

    // Prevent sprite from intercepting raycasts — its 2m width
    // extends beyond small zones and causes misselection in top-down view
    sprite.raycast = () => {}

    return sprite
  }

  /**
   * Draw rounded rectangle path
   */
  private roundRect(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number
  ): void {
    ctx.beginPath()
    ctx.moveTo(x + radius, y)
    ctx.lineTo(x + width - radius, y)
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius)
    ctx.lineTo(x + width, y + height - radius)
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height)
    ctx.lineTo(x + radius, y + height)
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius)
    ctx.lineTo(x, y + radius)
    ctx.quadraticCurveTo(x, y, x + radius, y)
    ctx.closePath()
  }

  /**
   * Get contrasting text color (black or white) based on background
   */
  private getContrastColor(hex: number): string {
    const r = (hex >> 16) & 0xff
    const g = (hex >> 8) & 0xff
    const b = hex & 0xff
    // Calculate relative luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
    return luminance > 0.5 ? '#000000' : '#ffffff'
  }

  /**
   * Add thick tube edges to the zone for better visibility
   */
  private addEdgeTubes(
    group: THREE.Group,
    width: number,
    height: number,
    depth: number,
    color: number
  ): void {
    const material = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: ZONE_OPACITY + 0.3,
    })

    const hw = width / 2
    const hh = height / 2
    const hd = depth / 2
    const radius = ZONE_OUTLINE_WIDTH

    // Helper to create tube between two points
    const createTube = (p1: THREE.Vector3, p2: THREE.Vector3): THREE.Mesh => {
      const curve = new THREE.LineCurve3(p1, p2)
      const tubeGeometry = new THREE.TubeGeometry(curve, 1, radius, 8, false)
      return new THREE.Mesh(tubeGeometry, material)
    }

    // Bottom face edges
    group.add(createTube(new THREE.Vector3(-hw, -hh, -hd), new THREE.Vector3(hw, -hh, -hd)))
    group.add(createTube(new THREE.Vector3(hw, -hh, -hd), new THREE.Vector3(hw, -hh, hd)))
    group.add(createTube(new THREE.Vector3(hw, -hh, hd), new THREE.Vector3(-hw, -hh, hd)))
    group.add(createTube(new THREE.Vector3(-hw, -hh, hd), new THREE.Vector3(-hw, -hh, -hd)))

    // Top face edges
    group.add(createTube(new THREE.Vector3(-hw, hh, -hd), new THREE.Vector3(hw, hh, -hd)))
    group.add(createTube(new THREE.Vector3(hw, hh, -hd), new THREE.Vector3(hw, hh, hd)))
    group.add(createTube(new THREE.Vector3(hw, hh, hd), new THREE.Vector3(-hw, hh, hd)))
    group.add(createTube(new THREE.Vector3(-hw, hh, hd), new THREE.Vector3(-hw, hh, -hd)))

    // Vertical edges
    group.add(createTube(new THREE.Vector3(-hw, -hh, -hd), new THREE.Vector3(-hw, hh, -hd)))
    group.add(createTube(new THREE.Vector3(hw, -hh, -hd), new THREE.Vector3(hw, hh, -hd)))
    group.add(createTube(new THREE.Vector3(hw, -hh, hd), new THREE.Vector3(hw, hh, hd)))
    group.add(createTube(new THREE.Vector3(-hw, -hh, hd), new THREE.Vector3(-hw, hh, hd)))
  }

  /**
   * Add thick tube edges for a polygon zone (bottom face, top face, verticals + corner spheres)
   */
  private addPolygonEdgeTubes(
    group: THREE.Group,
    points: Array<{ x: number; y: number }>,
    height: number,
    color: number
  ): void {
    const material = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: ZONE_OPACITY + 0.3,
    })

    const radius = ZONE_OUTLINE_WIDTH
    const sphereGeo = new THREE.SphereGeometry(radius, 8, 8)

    const createTube = (p1: THREE.Vector3, p2: THREE.Vector3): THREE.Mesh => {
      const curve = new THREE.LineCurve3(p1, p2)
      const tubeGeometry = new THREE.TubeGeometry(curve, 1, radius, 8, false)
      return new THREE.Mesh(tubeGeometry, material)
    }

    for (let i = 0; i < points.length; i++) {
      const curr = points[i]
      const next = points[(i + 1) % points.length]

      const bottomCurr = new THREE.Vector3(curr.x * DPM_SCALE, 0, curr.y * DPM_SCALE)
      const bottomNext = new THREE.Vector3(next.x * DPM_SCALE, 0, next.y * DPM_SCALE)
      const topCurr = new THREE.Vector3(curr.x * DPM_SCALE, height, curr.y * DPM_SCALE)
      const topNext = new THREE.Vector3(next.x * DPM_SCALE, height, next.y * DPM_SCALE)

      // Bottom edge
      group.add(createTube(bottomCurr, bottomNext))
      // Top edge
      group.add(createTube(topCurr, topNext))
      // Vertical edge
      group.add(createTube(bottomCurr, topCurr))

      // Corner spheres
      const bottomSphere = new THREE.Mesh(sphereGeo, material)
      bottomSphere.position.copy(bottomCurr)
      group.add(bottomSphere)

      const topSphere = new THREE.Mesh(sphereGeo, material)
      topSphere.position.copy(topCurr)
      group.add(topSphere)
    }
  }

  /**
   * Add thick tube edges for an ellipse zone (bottom ring, top ring, vertical pillars)
   */
  private addEllipseEdgeTubes(
    group: THREE.Group,
    radiusX: number,
    radiusZ: number,
    height: number,
    color: number
  ): void {
    const material = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: ZONE_OPACITY + 0.3,
    })

    const radius = ZONE_OUTLINE_WIDTH
    const segments = 48

    // Generate ellipse points
    const ellipsePoints: THREE.Vector3[] = []
    for (let i = 0; i <= segments; i++) {
      const angle = (i / segments) * Math.PI * 2
      ellipsePoints.push(new THREE.Vector3(
        Math.cos(angle) * radiusX,
        0,
        Math.sin(angle) * radiusZ
      ))
    }

    // Bottom ring tube
    const bottomCurve = new THREE.CatmullRomCurve3(ellipsePoints, true)
    const bottomTubeGeometry = new THREE.TubeGeometry(bottomCurve, segments, radius, 8, true)
    const bottomTube = new THREE.Mesh(bottomTubeGeometry, material)
    group.add(bottomTube)

    // Top ring tube
    const topPoints = ellipsePoints.map(p => new THREE.Vector3(p.x, height, p.z))
    const topCurve = new THREE.CatmullRomCurve3(topPoints, true)
    const topTubeGeometry = new THREE.TubeGeometry(topCurve, segments, radius, 8, true)
    const topTube = new THREE.Mesh(topTubeGeometry, material)
    group.add(topTube)

    // 4 vertical pillars at cardinal points
    const createTube = (p1: THREE.Vector3, p2: THREE.Vector3): THREE.Mesh => {
      const curve = new THREE.LineCurve3(p1, p2)
      const tubeGeometry = new THREE.TubeGeometry(curve, 1, radius, 8, false)
      return new THREE.Mesh(tubeGeometry, material)
    }

    const cardinals = [
      new THREE.Vector3(radiusX, 0, 0),
      new THREE.Vector3(-radiusX, 0, 0),
      new THREE.Vector3(0, 0, radiusZ),
      new THREE.Vector3(0, 0, -radiusZ),
    ]
    for (const p of cardinals) {
      group.add(createTube(p, new THREE.Vector3(p.x, height, p.z)))
    }
  }

  /**
   * Update a single zone
   */
  updateZone(zone: Zone): void {
    this.removeZone(zone.id)
    this.renderZone(zone)
  }

  /**
   * Remove a zone by ID
   */
  removeZone(id: string): void {
    const mesh = this.meshes.get(id)
    if (mesh) {
      this.group.remove(mesh)
      this.disposeMesh(mesh)
      this.meshes.delete(id)
      debug.log('ZoneRenderer', `Removed zone: ${id}`)
    }
  }

  /**
   * Dispose of a mesh and its resources
   */
  private disposeMesh(object: THREE.Object3D): void {
    object.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.geometry?.dispose()
        if (child.material instanceof THREE.Material) {
          child.material.dispose()
        } else if (Array.isArray(child.material)) {
          child.material.forEach(m => m.dispose())
        }
      } else if (child instanceof THREE.LineSegments) {
        child.geometry?.dispose()
        if (child.material instanceof THREE.Material) {
          child.material.dispose()
        }
      } else if (child instanceof THREE.Sprite) {
        child.material.map?.dispose()
        child.material.dispose()
      }
    })
  }

  /**
   * Clear all zones
   */
  clear(): void {
    for (const mesh of this.meshes.values()) {
      this.group.remove(mesh)
      this.disposeMesh(mesh)
    }
    this.meshes.clear()
    debug.log('ZoneRenderer', 'Cleared all zones')
  }

  /**
   * Get a zone mesh by ID
   */
  getMesh(zoneId: string): THREE.Object3D | undefined {
    return this.meshes.get(zoneId)
  }

  /**
   * Get zone count
   */
  get count(): number {
    return this.meshes.size
  }

  /**
   * Set visibility of all zones
   */
  setVisible(visible: boolean): void {
    this.group.visible = visible
  }

  /**
   * Check if zones are visible
   */
  isVisible(): boolean {
    return this.group.visible
  }

  /**
   * Dispose renderer
   */
  dispose(): void {
    this.clear()
    const threeScene = this.scene.getScene()
    threeScene.remove(this.group)
    debug.log('ZoneRenderer', 'Disposed')
  }
}
